/**
 * Viu HE is configured per ISO country (Ghana, Kenya, …).
 *
 * Preferred: set `VUE_APP_VIU_REGIONS_JSON` to a JSON array of region objects.
 * Each object should include at least `countryCode`, `apiBaseUrl`, and `productCode`.
 * Optional: `heAction`, `defaultCountryDialCode` — omitted fields fall back to flat `VUE_APP_VIU_*`.
 * AES key/IV are global: `VUE_APP_VIU_AES_KEY` / `VUE_APP_VIU_AES_IV` on `config.viu` (not per region).
 *
 * When `VUE_APP_VIU_REGIONS_JSON` is unset or invalid, a single Ghana (GH) row is built
 * from the legacy flat env vars so existing deployments keep working.
 *
 * Geo bypass (QA): when a region’s `clientRedirectBypassGeo` is true (see below),
 * `VUE_APP_VIU_BYPASS_GEO_REGION=GH` or `KE` picks which partner URL to open. If unset,
 * the first region in the array is used.
 *
 * `clientRedirectBypassGeo` per region (first match wins):
 * 1. JSON field `clientRedirectBypassGeo` on that row
 * 2. `VUE_APP_VIU_<ISO>_REDIRECT_BYPASS_GEO=true` (e.g. `VUE_APP_VIU_KE_REDIRECT_BYPASS_GEO`)
 * 3. `VUE_APP_VIU_REDIRECT_BYPASS_GEO=true` (applies to every region row if not overridden above)
 *
 * Multiple carriers sharing one country (e.g. MTN Ghana + Telecel Ghana are both `GH`):
 * IP geolocation only resolves the country, not the operator, so a country can have more than
 * one region row as long as each carrier row is tagged with an optional `carrier` field (e.g.
 * `"carrier":"TELECEL"`). At most one row per country should be left untagged — that row is the
 * country's default/fallback when the operator can't be determined. See `viuHeaderEnrichment.js`
 * for how the carrier is resolved (URL override, then IP ASN via `VUE_APP_VIU_CARRIER_ASN_MAP_JSON`).
 *
 * Optional per-row `xApiKey`: some partners issue a distinct `x-api-key` per carrier for the SAB
 * login call (`sab.loginWithViuMsisdn`). When a row provides one, it's used instead of the global
 * `VUE_APP_VIU_X_API_KEY` for logins resolved against that specific carrier (see
 * `resolveActiveViuRegionXApiKey` in `viuHeaderEnrichment.js`). Omit it to keep using the global key.
 */

function trim(s) {
  return s != null ? String(s).trim() : ''
}

/** Fallback dial codes when a region row omits `defaultCountryDialCode` (extend for new markets). */
const DEFAULT_DIAL_BY_ISO = {
  GH: '233',
  KE: '254'
}

/**
 * @param {Record<string, unknown>} row
 * @param {string} countryCode ISO alpha-2
 */
function mergeRegionClientRedirectBypassGeo(row) {
  if (row && row.clientRedirectBypassGeo != null && trim(row.clientRedirectBypassGeo) !== '') {
    const v = row.clientRedirectBypassGeo
    return v === true || String(v).toLowerCase() === 'true'
  }
  return false
}

function dialForRegionFromEnv(countryCode) {
  const code = String(countryCode || '').toUpperCase()
  const per =
    trim(process.env[`VUE_APP_VIU_${code}_DEFAULT_COUNTRY_DIAL`]) ||
    trim(process.env[`VUE_APP_VIU_DEFAULT_COUNTRY_DIAL_${code}`])
  if (per) {
    return per
  }
  return (
    trim(process.env.VUE_APP_VIU_DEFAULT_COUNTRY_DIAL) ||
    DEFAULT_DIAL_BY_ISO[code] ||
    ''
  )
}

function mergeRegionRow(row) {
  const countryCode = trim(row.countryCode || row.code || '').toUpperCase()
  if (!countryCode) {
    return null
  }
  const apiBaseUrl =
    trim(row.apiBaseUrl || '') || trim(process.env.VUE_APP_VIU_API_BASE_URL || '')
  const productCode =
    trim(row.productCode || '') || trim(process.env.VUE_APP_VIU_PRODUCT_CODE || 'TEST')
  const heAction = trim(row.heAction || '') || trim(process.env.VUE_APP_VIU_HE_ACTION || 'HE')
  const defaultCountryDialCode =
    trim(row.defaultCountryDialCode || '') ||
    dialForRegionFromEnv(countryCode) ||
    ''

  const clientRedirectBypassGeo = mergeRegionClientRedirectBypassGeo(row)

  /** Optional operator tag disambiguating multiple rows for the same country (see file header). */
  const carrier = trim(row.carrier || '').toUpperCase() || null

  /** Optional per-carrier x-api-key for the SAB login call (see file header). */
  const xApiKey = trim(row.xApiKey || '') || null

  return {
    countryCode,
    apiBaseUrl,
    productCode,
    heAction: heAction || 'HE',
    defaultCountryDialCode,
    clientRedirectBypassGeo,
    carrier,
    xApiKey
  }
}

/**
 * @returns {Array<{ countryCode: string, apiBaseUrl: string, productCode: string, heAction: string, defaultCountryDialCode: string, clientRedirectBypassGeo: boolean, carrier: string|null, xApiKey: string|null }>}
 */
export function buildViuRegionsFromEnv() {
  const rawJson = trim(process.env.VUE_APP_VIU_REGIONS_JSON)
  if (rawJson) {
    try {
      const parsed = JSON.parse(rawJson)
      if (Array.isArray(parsed) && parsed.length > 0) {
        const merged = []
        for (const row of parsed) {
          if (!row || typeof row !== 'object') {
            continue
          }
          const m = mergeRegionRow(row)
          if (m) {
            merged.push(m)
          }
        }
        if (merged.length) {
          return merged
        }
      }
    } catch {
      // fall through to legacy
    }
  }
}

/**
 * @param {{ countryCode: string }[]} regions
 * @returns {string} ISO country code for outbound HE when geo is bypassed
 */
export function getViuBypassDefaultCountryCode(regions) {
  if (!regions || !regions.length) {
    return 'GH'
  }
  const fromEnv = trim(process.env.VUE_APP_VIU_BYPASS_GEO_REGION || '').toUpperCase()
  if (fromEnv && regions.some(r => r.countryCode === fromEnv)) {
    return fromEnv
  } else if (!fromEnv && regions.some(r => r.clientRedirectBypassGeo === true)) {
    return regions.find(r => r.clientRedirectBypassGeo === true)?.countryCode || 'GH'
  }
  return regions[0].countryCode
}

/**
 * Optional ASN → carrier map for disambiguating operators that share one country's IP geolocation
 * (e.g. MTN Ghana vs Telecel Ghana — both resolve to `GH`). Keys are `AS<number>` (case-insensitive,
 * `AS` prefix optional in the JSON); values are carrier codes matched against a region row's
 * `carrier` field (see `mergeRegionRow` above).
 *
 * JSON must be ONE line, e.g.:
 * VUE_APP_VIU_CARRIER_ASN_MAP_JSON={"AS37067":"MTN","AS30988":"TELECEL"}
 *
 * When unset/invalid, returns `{}` and carrier detection from IP falls back to the URL override
 * (`?carrier=` / `?operator=`) or, failing that, to a country's untagged/default region row.
 *
 * @returns {Record<string, string>}
 */
export function buildViuCarrierAsnMapFromEnv() {
  const raw = trim(process.env.VUE_APP_VIU_CARRIER_ASN_MAP_JSON)
  if (!raw) {
    return {}
  }
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }
    const map = {}
    for (const [asnRaw, carrierRaw] of Object.entries(parsed)) {
      const asnTrimmed = trim(asnRaw).toUpperCase()
      const asn = asnTrimmed && !asnTrimmed.startsWith('AS') ? `AS${asnTrimmed}` : asnTrimmed
      const carrier = trim(carrierRaw).toUpperCase()
      if (asn && carrier) {
        map[asn] = carrier
      }
    }
    return map
  } catch {
    return {}
  }
}
